// src/pages/BoneForge.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Teknik Proje & Oyun Paylaşım Merkezi  ·  BoneForge
// Veri: Firebase Realtime DB /forge
// Özellikler:
//   • Mavi / Elektrik tema (#52a2ff)
//   • Tür seçimi: Oyun / Kod / Script / Diğer
//   • Platform badge + otomatik URL tespiti (GitHub, Itch.io, vb.)
//   • Desteklenen platformlar grid — BoneBeat yaklaşımı
//   • Açıklama: min 10 kelime, max 300 kelime — "Forge It" butonu kontrollü
//   • Her kart → "Forged by: [DisplayName]" + tarih → /profile/:uid
//   • Blueprint grid estetiği hover'da
//   • i18next tam uyumu
//   • authLoading koruması + onError callback
//   • Admin: moderasyon paneli + kullanıcı engelleme
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState }        from "react";
import { useNavigate }                        from "react-router-dom";
import { useTranslation }                     from "react-i18next";
import { ref, onValue, push, update, remove } from "firebase/database";
import { db }                                 from "../firebase/config";
import { useAuth }                            from "../context/AuthContext";
import CommentSection                         from "../components/CommentSection";
import SidebarForge                           from "../components/Sidebar/SidebarForge.jsx";
import { SidebarForgeProvider }               from "../context/SidebarForgeContext.jsx";
import { filterText }                          from "../utils/profanityFilter";

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK: uid ile canlı kullanıcı bilgisi — isim/foto değişince otomatik güncellenir
// ═══════════════════════════════════════════════════════════════════════════════
function useUserInfo(uid) {
  const [info, setInfo] = useState({ displayName: null, photoURL: null });
  useEffect(() => {
    if (!uid) return;
    const r = ref(db, `publicProfiles/${uid}`);
    const unsub = onValue(r, snap => {
      if (snap.exists()) {
        const d = snap.val();
        setInfo({ displayName: d.displayName || null, photoURL: d.photoURL || null });
      }
    });
    return () => unsub();
  }, [uid]);
  return info;
}

// ═══════════════════════════════════════════════════════════════════════════════
// RENK PALETİ — Elektrik Mavi Tema
// ═══════════════════════════════════════════════════════════════════════════════

const C = {
  bg:         "#08090f",            // Derin lacivert-siyah
  bgCard:     "#0d0e1a",
  bgDeep:     "#04050a",
  bgInput:    "#0f1020",
  border:     "rgba(82,162,255,0.13)",
  borderHov:  "rgba(82,162,255,0.4)",
  borderFaint:"rgba(82,162,255,0.07)",
  blue:       "#52a2ff",            // Ana mavi
  blueDim:    "rgba(82,162,255,0.5)",
  blueGhost:  "var(--bone-text-muted)",
  blueFaint:  "rgba(82,162,255,0.08)",
  cyan:       "#7dd3fc",
  white:      "#e8f0ff",            // Başlık metin
  whiteDim:   "rgba(232,240,255,0.6)",
  whiteGhost: "rgba(232,240,255,0.22)",
  red:        "#f87171",
  redDim:     "rgba(248,113,113,0.55)",
  green:      "#4ade80",
};

// ── Kelime sınırları ──────────────────────────────────────────────────────────
const MIN_WORDS = 10;
const MAX_WORDS = 300;

// ── Platform listesi ─────────────────────────────────────────────────────────
const PLATFORMS = [
  { name: "GitHub",       regex: /^https?:\/\/(www\.)?github\.com\//,          icon: "⌥", color: "#e8f0ff" },
  { name: "Itch.io",      regex: /^https?:\/\/([a-z0-9-]+\.)?itch\.io\//,      icon: "◈", color: "#fa5c5c" },
  { name: "GitLab",       regex: /^https?:\/\/(www\.)?gitlab\.com\//,          icon: "◆", color: "#fc6d26" },
  { name: "Bitbucket",    regex: /^https?:\/\/(www\.)?bitbucket\.org\//,       icon: "◉", color: "#2684ff" },
  { name: "CodePen",      regex: /^https?:\/\/(www\.)?codepen\.io\//,          icon: "✏", color: "#47cf73" },
  { name: "Replit",       regex: /^https?:\/\/(www\.)?replit\.com\//,          icon: "▶", color: "#f26207" },
  { name: "Glitch",       regex: /^https?:\/\/(www\.)?glitch\.com\//,          icon: "✦", color: "#a370f7" },
  { name: "Game Jolt",    regex: /^https?:\/\/(www\.)?gamejolt\.com\//,        icon: "⬢", color: "#ccff00" },
  { name: "Newgrounds",   regex: /^https?:\/\/(www\.)?newgrounds\.com\//,      icon: "⚑", color: "#f7a02e" },
  { name: "SourceForge",  regex: /^https?:\/\/(www\.)?sourceforge\.net\//,     icon: "⬡", color: "#ff6600" },
  { name: "Hackaday",     regex: /^https?:\/\/(www\.)?hackaday\.io\//,         icon: "⚡", color: "#1aad4b" },
  { name: "Kongregate",   regex: /^https?:\/\/(www\.)?kongregate\.com\//,      icon: "♟", color: "#ff6600" },
];

const PROJECT_TYPES = [
  { key: "game",   icon: "◈" },
  { key: "code",   icon: "⌥" },
  { key: "script", icon: "⚙" },
  { key: "other",  icon: "◆" },
];

function detectPlatform(url = "") { return PLATFORMS.find(p => p.regex.test(url)) || null; }

function countWords(text = "") {
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts, m = Math.floor(diff / 60000);
  if (m < 1)  return "şimdi";
  if (m < 60) return `${m}dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}sa önce`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}g önce`;
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}
function fullDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI BİLEŞENLERİ — Mavi tema
// ═══════════════════════════════════════════════════════════════════════════════

function IconBtn({ onClick, title, danger, children, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: "none", border: "none", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", color: danger ? (hov ? C.red : C.redDim) : (hov ? C.blue : C.blueDim), padding: "4px 6px", display: "flex", alignItems: "center", transition: "color 0.2s", opacity: disabled ? 0.4 : 1 }}>
      {children}
    </button>
  );
}

function PrimaryBtn({ onClick, children, disabled, small }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: disabled ? "rgba(82,162,255,0.15)" : hov ? "#79b8ff" : C.blue, border: `1px solid ${disabled ? C.border : C.blue}`, borderRadius: 8, color: disabled ? C.blueDim : C.bg, padding: small ? "8px 16px" : "11px 24px", fontFamily: "'Courier Prime',monospace", fontWeight: "bold", fontSize: small ? 10 : 11, letterSpacing: "0.2em", textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer", transition: "all 0.2s", opacity: disabled ? 0.6 : 1 }}>
      {children}
    </button>
  );
}

function GhostBtn({ onClick, children, danger }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: "transparent", border: `1px solid ${danger ? (hov ? C.red : C.redDim) : (hov ? C.blue : C.border)}`, borderRadius: 8, color: danger ? (hov ? C.red : C.redDim) : (hov ? C.blue : C.blueDim), padding: "8px 16px", fontFamily: "'Courier Prime',monospace", fontWeight: "bold", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s" }}>
      {children}
    </button>
  );
}

function TextInput({ value, onChange, placeholder, disabled, onKeyDown }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} onKeyDown={onKeyDown}
      style={{ display: "block", width: "100%", boxSizing: "border-box", padding: "10px 13px", borderRadius: 6, background: disabled ? C.blueFaint : C.bgInput, border: `1px solid ${C.border}`, color: disabled ? C.blueDim : C.white, fontFamily: "'Courier Prime',monospace", fontSize: 12, outline: "none", letterSpacing: "0.04em", transition: "border-color 0.2s" }}
      onFocus={e => e.target.style.borderColor = C.borderHov}
      onBlur={e  => e.target.style.borderColor = C.border}
    />
  );
}

function FieldLabel({ children, required }) {
  return (
    <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: C.blueDim, marginBottom: 7, display: "flex", alignItems: "center", gap: 4 }}>
      {children}
      {required && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
    </div>
  );
}

function PlatformBadge({ url, size = "sm" }) {
  const p = detectPlatform(url);
  if (!p) return null;
  return (
    <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: size === "lg" ? 9 : 8, letterSpacing: "0.18em", textTransform: "uppercase", color: p.color, border: `1px solid ${p.color}44`, borderRadius: 6, background: `${p.color}0d`, padding: size === "lg" ? "3px 10px" : "2px 7px", lineHeight: 1.4 }}>
      {p.icon} {p.name}
    </span>
  );
}

function TypeBadge({ typeKey, label }) {
  const t = PROJECT_TYPES.find(x => x.key === typeKey) || PROJECT_TYPES[3];
  return (
    <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, letterSpacing: "0.18em", textTransform: "uppercase", color: C.blueDim, border: `1px solid ${C.border}`, borderRadius: 6, padding: "2px 7px", lineHeight: 1.4 }}>
      {t.icon} {label}
    </span>
  );
}

function BlueRule({ opacity = 0.08 }) {
  return <div style={{ height: 1, margin: "4px 0", background: `linear-gradient(90deg,transparent,rgba(82,162,255,${opacity}),transparent)` }} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FORGE KARTI — Blueprint mavi estetiği
// ═══════════════════════════════════════════════════════════════════════════════

function ForgeCard({ project, onDelete, onBlockUser, isAdmin, currentUid }) {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const [hov, setHov] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const isOwner   = project.uid === currentUid;
  const platform  = detectPlatform(project.url || "");
  const wordCount = countWords(project.description || "");
  const typeLabel = t(`boneforge.types.${project.type}`, project.type || "");
  const liveUser  = useUserInfo(project.uid);
  const displayName = liveUser.displayName || project.displayName || t('boneforge.traveler');
  const photoURL    = liveUser.photoURL    || project.photoURL    || null;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: C.bgCard,
        backgroundImage: hov
          ? `linear-gradient(rgba(82,162,255,0.025) 1px, transparent 1px),
             linear-gradient(90deg, rgba(82,162,255,0.025) 1px, transparent 1px)`
          : "none",
        backgroundSize: "20px 20px",
        border: `1px solid ${hov ? C.borderHov : C.border}`,
        borderRadius: 8,
        borderTop: `2px solid ${platform ? platform.color + "99" : (hov ? C.blue : "rgba(82,162,255,0.25)")}`,
        padding: "20px 20px 16px",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease, background-image 0.3s ease",
        boxShadow: hov
          ? `0 0 28px rgba(82,162,255,0.1), inset 0 0 60px rgba(82,162,255,0.02)`
          : "0 2px 14px rgba(0,0,0,0.55)",
        overflow: "hidden",
      }}
    >
      {/* Blueprint köşe ID */}
      <span style={{ position: "absolute", bottom: 6, right: 8, fontFamily: "'Courier Prime',monospace", fontSize: 7, color: "rgba(82,162,255,0.12)", letterSpacing: "0.1em", pointerEvents: "none", opacity: hov ? 1 : 0, transition: "opacity 0.3s" }}>
        [{project.id?.slice(-4).toUpperCase()}]
      </span>

      {/* Üst: isim + butonlar */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.05rem", fontWeight: 700, color: hov ? C.cyan : C.blue, letterSpacing: "0.04em", marginBottom: 6, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 0.3s" }}>
            {project.name || t('boneforge.unnamed')}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <TypeBadge typeKey={project.type} label={typeLabel} />
            {project.url && <PlatformBadge url={project.url} />}
          </div>
        </div>

        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {isAdmin && !isOwner && (
            <IconBtn danger title={t('boneforge.blockUser')}
              onClick={e => { e.stopPropagation(); onBlockUser(project.uid, project.displayName); }}>
              <svg viewBox="0 0 24 24" fill="currentColor" width={13} height={13}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" /></svg>
            </IconBtn>
          )}
          {(isAdmin || isOwner) && (
            <IconBtn danger title={t('common.delete')}
              onClick={e => { e.stopPropagation(); onDelete(project.id, project.name); }}>
              <svg viewBox="0 0 24 24" fill="currentColor" width={13} height={13}><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" /></svg>
            </IconBtn>
          )}
        </div>
      </div>

      {/* Link */}
      {project.url && (
        <a href={project.url} target="_blank" rel="noopener noreferrer"
          style={{ fontFamily: "'Courier Prime',monospace", fontSize: "0.68rem", color: "rgba(82,162,255,0.35)", letterSpacing: "0.06em", textDecoration: "none", display: "block", marginBottom: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", transition: "color 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.color = C.blueDim}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(82,162,255,0.35)"}
        >
          ↗ {project.url}
        </a>
      )}

      {/* Açıklama */}
      {project.description && (
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: "0.82rem", color: C.whiteDim, lineHeight: 1.75, marginBottom: 14, borderLeft: `2px solid rgba(82,162,255,0.2)`, paddingLeft: 10, display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {project.description}
        </p>
      )}

      {/* Kelime sayacı chip */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(82,162,255,0.3)", border: "1px solid rgba(82,162,255,0.12)", padding: "2px 6px" }}>
          {wordCount} {t('boneforge.words')}
        </span>
      </div>

      {/* Sosyal şerit: Forged by + tarih */}
      <BlueRule opacity={0.1} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, paddingTop: 8 }}>
        <button
          onClick={e => { e.stopPropagation(); navigate(`/profile/${project.uid}`); }}
          title={`${displayName} profiline git`}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "opacity 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.65"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          <div style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, background: "rgba(82,162,255,0.1)", border: `1px solid rgba(82,162,255,0.22)`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 9, color: C.blueDim }}>{displayName[0].toUpperCase()}</span>
            }
          </div>
          <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: "0.65rem", letterSpacing: "0.06em" }}>
            <span style={{ color: "rgba(82,162,255,0.35)" }}>{t('boneforge.forgedBy')} </span>
            <span style={{ color: "rgba(82,162,255,0.65)" }}>{displayName}</span>
          </span>
        </button>

        <span title={fullDate(project.createdAt)} style={{ fontFamily: "'Courier Prime',monospace", fontSize: "0.63rem", color: "rgba(82,162,255,0.28)", letterSpacing: "0.06em" }}>
          {timeAgo(project.createdAt)}
        </span>
      </div>

      <button
        type="button"
        onClick={() => setCommentsOpen(open => !open)}
        aria-expanded={commentsOpen}
        aria-controls={`comments-forge-${project.id}`}
        style={{ marginTop: 10, padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", color: C.whiteDim, cursor: "pointer", fontFamily: "'Courier Prime',monospace", fontSize: 10 }}
      >{t(commentsOpen ? "comments.hide" : "comments.show")}</button>
      {commentsOpen && (
        <div id={`comments-forge-${project.id}`}>
          <CommentSection type="forge" postId={project.id} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// YENİ PROJE FORMU
// ═══════════════════════════════════════════════════════════════════════════════

function NewProjectForm({ currentUser, currentUserData, onSuccess }) {
  const { t } = useTranslation();
  const [name,        setName]        = useState("");
  const [url,         setUrl]         = useState("");
  const [description, setDescription] = useState("");
  const [type,        setType]        = useState("game");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  const wordCount = countWords(description);
  const tooFew    = description.trim().length > 0 && wordCount < MIN_WORDS;
  const tooMany   = wordCount > MAX_WORDS;
  const wordOk    = wordCount >= MIN_WORDS && wordCount <= MAX_WORDS;
  const canForge  = name.trim().length > 0 && wordOk && !saving;

  const wordWarning = (tooFew || tooMany) && description.trim().length > 0
    ? t('boneforge.form.wordRange', { count: wordCount, min: MIN_WORDS, max: MAX_WORDS })
    : null;

  const wordCountColor = tooMany ? C.red
    : tooFew && description.trim().length > 0 ? C.redDim
    : wordOk ? C.green
    : "rgba(82,162,255,0.3)";

  const detectedPlatform = url ? detectPlatform(url) : null;

  // Desteklenen platformlar grid
  const PLATFORM_GRID = [
    { name: "GitHub",      color: "#e8f0ff" }, { name: "Itch.io",    color: "#fa5c5c" },
    { name: "GitLab",      color: "#fc6d26" }, { name: "Bitbucket",  color: "#2684ff" },
    { name: "CodePen",     color: "#47cf73" }, { name: "Replit",     color: "#f26207" },
    { name: "Glitch",      color: "#a370f7" }, { name: "Game Jolt",  color: "#ccff00" },
    { name: "Newgrounds",  color: "#f7a02e" }, { name: "SourceForge",color: "#ff6600" },
    { name: "Hackaday",    color: "#1aad4b" }, { name: "Kongregate", color: "#ff6600" },
  ];
  function getPlatformIcon(name) { return PLATFORMS.find(p => p.name === name)?.icon || "⌥"; }

  async function handleForge() {
    if (!canForge) return;
    if (!currentUser?.uid) {
      setError(t('boneforge.form.noSession'));
      console.error("[BoneForge] handleForge: uid yok", currentUser);
      return;
    }
    setSaving(true); setError("");
    try {
      const payload = {
        name:        filterText(name.trim()),
        url:         url.trim(),
        description: filterText(description.trim()),
        type,
        uid:         currentUser.uid,
        displayName: currentUser.displayName || currentUser.email || t('boneforge.traveler'),
        photoURL:    currentUserData?.photoURL || currentUser.photoURL || "",
        createdAt:   Date.now(),
      };
      console.log("[BoneForge] Proje oluşturuluyor:", payload);
      await push(ref(db, "forge"), payload);
      console.log("[BoneForge] Proje kaydedildi.");
      setName(""); setUrl(""); setDescription(""); setType("game");
      onSuccess();
    } catch (err) {
      console.error("[BoneForge] Hata:", err.code, err.message);
      if (err.code === "PERMISSION_DENIED") setError(t('boneforge.form.permissionError'));
      else if (err.message?.includes("network")) setError(t('boneforge.form.networkError'));
      else setError(`${t('boneforge.form.error')} (${err.code || "bilinmeyen"})`);
    }
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.38em", color: C.blueGhost, textTransform: "uppercase", marginBottom: 6 }}>
        ⚒ {t('boneforge.form.eyebrow')} ⚒
      </p>
      <h1 style={{ fontFamily: "'Playfair Display',serif", color: C.blue, fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
        {t('boneforge.form.title')}
      </h1>
      <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueGhost, letterSpacing: "0.04em", marginBottom: 32, lineHeight: 1.7 }}>
        {t('boneforge.form.subtitle')}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

        {/* Tür seçimi */}
        <div>
          <FieldLabel>{t('boneforge.form.typeLabel')}</FieldLabel>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {PROJECT_TYPES.map(pt => (
              <button key={pt.key} onClick={() => setType(pt.key)}
                style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", padding: "8px 14px", borderRadius: 6, cursor: "pointer", transition: "all 0.2s", background: type === pt.key ? "rgba(82,162,255,0.12)" : "transparent", border: `1px solid ${type === pt.key ? C.blue : C.border}`, color: type === pt.key ? C.blue : C.blueDim }}>
                {pt.icon} {t(`boneforge.types.${pt.key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Proje adı */}
        <div>
          <FieldLabel required>{t('boneforge.form.nameLabel')}</FieldLabel>
          <TextInput value={name} onChange={e => setName(e.target.value)} placeholder={t('boneforge.form.namePlaceholder')} />
        </div>

        {/* Link */}
        <div>
          <FieldLabel>{t('boneforge.form.urlLabel')}</FieldLabel>
          <TextInput value={url} onChange={e => setUrl(e.target.value)} placeholder={t('boneforge.form.urlPlaceholder')} />
          {url && !detectedPlatform && (
            <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.blueGhost, margin: "5px 0 0" }}>{t('boneforge.form.unknownPlatform')}</p>
          )}
          {url && detectedPlatform && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
              <PlatformBadge url={url} size="lg" />
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.green, letterSpacing: "0.06em" }}>✓ {t('boneforge.form.detected')}</span>
            </div>
          )}
        </div>

        {/* Açıklama */}
        <div>
          <FieldLabel required>{t('boneforge.form.descLabel', { min: MIN_WORDS, max: MAX_WORDS })}</FieldLabel>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('boneforge.form.descPlaceholder')}
            rows={7}
            style={{ display: "block", width: "100%", boxSizing: "border-box", padding: "10px 13px", borderRadius: 6, resize: "vertical", background: C.bgInput, border: `1px solid ${wordWarning ? C.redDim : C.border}`, color: C.white, fontFamily: "'Courier Prime',monospace", fontSize: 12, outline: "none", letterSpacing: "0.04em", lineHeight: 1.75, transition: "border-color 0.2s" }}
            onFocus={e => e.target.style.borderColor = wordWarning ? C.redDim : C.borderHov}
            onBlur={e  => e.target.style.borderColor = wordWarning ? C.redDim : C.border}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
            {wordWarning
              ? <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: C.red, letterSpacing: "0.04em" }}>⚠ {wordWarning}</span>
              : <span />
            }
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: wordCountColor, letterSpacing: "0.1em" }}>
              {wordCount} / {MAX_WORDS} {t('boneforge.words')}
            </span>
          </div>
        </div>

        {/* Desteklenen platformlar */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.24em", color: "rgba(82,162,255,0.22)", textTransform: "uppercase" }}>{t('boneforge.form.supportedPlatforms')}</span>
            <div style={{ flex: 1, height: 1, background: C.borderFaint }} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {PLATFORM_GRID.map(p => (
              <span key={p.name} style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.1em", padding: "3px 9px", borderRadius: 6, border: `1px solid ${p.color}22`, color: `${p.color}bb`, background: `${p.color}0d` }}>
                {getPlatformIcon(p.name)} {p.name}
              </span>
            ))}
          </div>
        </div>

        {error && <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.red, letterSpacing: "0.04em", lineHeight: 1.6 }}>⚠ {error}</p>}

        {/* Forge It */}
        <div>
          <PrimaryBtn onClick={handleForge} disabled={!canForge}>
            {saving ? `⚒ ${t('boneforge.form.forging')}` : `⚒ ${t('boneforge.form.forgeIt')}`}
          </PrimaryBtn>
          {!wordOk && name.trim().length > 0 && (
            <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(82,162,255,0.32)", letterSpacing: "0.06em", marginTop: 8 }}>
              {t('boneforge.form.waitForDesc')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODERASYON PANELİ
// ═══════════════════════════════════════════════════════════════════════════════

function ModerationPanel({ projects, onDelete, onBlockUser }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const filtered = projects.filter(p =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.displayName || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.uid || "").includes(search)
  );
  return (
    <div>
      <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.38em", color: "rgba(248,113,113,0.45)", textTransform: "uppercase", marginBottom: 6 }}>⚒ Admin ⚒</p>
      <h1 style={{ fontFamily: "'Playfair Display',serif", color: C.red, fontSize: 28, fontWeight: 700, marginBottom: 8 }}>{t('boneforge.mod.title')}</h1>
      <BlueRule opacity={0.1} />

      <div style={{ display: "flex", gap: 12, margin: "20px 0", flexWrap: "wrap" }}>
        {[
          { label: t('boneforge.mod.totalProjects'), val: projects.length },
          { label: t('bonebeat.mod.uniqueUsers'),    val: new Set(projects.map(p => p.uid)).size },
          { label: t('boneforge.mod.platformCount'), val: new Set(projects.map(p => detectPlatform(p.url)?.name).filter(Boolean)).size },
        ].map(stat => (
            <div key={stat.label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 20px", flex: 1, minWidth: 120 }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: C.blue }}>{stat.val}</div>
            <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.blueGhost, letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder={t('boneforge.mod.searchPlaceholder')} />
      </div>

      <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div className="bf-mod-row" style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
          {[t('boneforge.mod.colName'), t('bonebeat.mod.colBy'), t('boneframe.mod.colPlatform'), t('bonebeat.mod.colAction')].map(h => (
            <span key={h} style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, color: C.blueGhost, letterSpacing: "0.25em", textTransform: "uppercase" }}>{h}</span>
          ))}
        </div>
        {filtered.length === 0 && <div style={{ padding: "24px", textAlign: "center", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueGhost }}>{t('bonebeat.mod.noResults')}</div>}
        {filtered.map((p, i) => {
          const pl = detectPlatform(p.url || "");
          return (
            <div key={p.id} className="bf-mod-row" style={{ padding: "12px 16px", alignItems: "center", borderBottom: i < filtered.length - 1 ? `1px solid ${C.borderFaint}` : "none" }}>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: C.white, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name || "—"}</span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.whiteDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.displayName || "—"}</span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: pl?.color || C.blueGhost }}>{pl ? `${pl.icon} ${pl.name}` : "—"}</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <GhostBtn onClick={() => onBlockUser(p.uid, p.displayName)} danger>{t('admin.block')}</GhostBtn>
                <GhostBtn onClick={() => onDelete(p.id, p.name)} danger>{t('common.delete')}</GhostBtn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA MODÜL — BoneForgeInner
// ═══════════════════════════════════════════════════════════════════════════════

function BoneForgeInner() {
  const { user, userData, isAdmin: ctxAdmin, authLoading = false } = useAuth();
  const { t } = useTranslation();
  const isAdmin = ctxAdmin || user?.isAdmin || false;

  const [projects,      setProjects]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [dbError,       setDbError]       = useState(null);
  const [view,          setView]          = useState("feed");
  const [toast,         setToast]         = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [blockConfirm,  setBlockConfirm]  = useState(null);

  // ── Firebase listener ─────────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    setDbError(null);

    const unsub = onValue(
      ref(db, "forge"),
      (snap) => {
        if (snap.exists()) {
          const list = Object.entries(snap.val())
            .map(([id, v]) => ({ id, ...v }))
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setProjects(list);
        } else {
          setProjects([]);
        }
        setLoading(false);
      },
      (err) => {
        console.error("[BoneForge] Firebase okuma hatası:", err.code, err.message);
        setDbError(err.message);
        setProjects([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [authLoading]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  async function handleDelete(id) {
    try { await remove(ref(db, `forge/${id}`)); console.log("[BoneForge] Silindi:", id); showToast(t('boneforge.toast.deleted')); }
    catch (err) { console.error("[BoneForge] Silme hatası:", err.code, err.message); showToast(t('boneforge.toast.deleteFailed')); }
    setConfirmDelete(null);
  }

  async function handleBlockUser(uid, displayName) {
    try { await update(ref(db, `users/${uid}`), { status: "blocked" }); showToast(`${displayName || uid} ${t('bonebeat.toast.blocked')}`); }
    catch (err) { console.error("[BoneForge] Engelleme hatası:", err.code, err.message); showToast(t('bonebeat.toast.blockFailed')); }
    setBlockConfirm(null);
  }

  const visibleProjects = view === "mine" ? projects.filter(p => p.uid === user?.uid) : projects;
  const isPageLoading   = authLoading || loading;

  const feedMeta = {
    feed: { pre: `⚒ ${t('boneforge.archiveEyebrow')} ⚒`, title: t('boneforge.archiveTitle') },
    mine: { pre: `⚒ ${t('boneforge.myEyebrow')} ⚒`,     title: t('boneforge.myTitle') },
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <SidebarForge activeView={view} onNavigate={setView} isAdmin={isAdmin} />

      {/* forg-main: BoneBeat gibi overlay — her cihazda tam genişlik */}
      <main className="forg-main" style={{ padding: "40px clamp(20px,4vw,60px)", minHeight: "100vh", overflowY: "auto" }}>

        {/* Toast */}
        {toast && (
          <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 9999, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 6, padding: "12px 20px", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.white, boxShadow: "0 8px 32px rgba(0,0,0,0.6)", letterSpacing: "0.06em" }}>
            {toast}
          </div>
        )}

        {/* Silme onayı */}
        {confirmDelete && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.white, marginBottom: 8 }}>{t('bonebeat.confirm.sure')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueDim, marginBottom: 24 }}>
                "<strong>{confirmDelete.name}</strong>" {t('bonebeat.confirm.deleteBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleDelete(confirmDelete.id)} danger>{t('common.delete')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* Engelleme onayı */}
        {blockConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 8 }}>{t('bonebeat.confirm.blockTitle')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueDim, marginBottom: 24 }}>
                <strong>{blockConfirm.name}</strong> {t('bonebeat.confirm.blockBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setBlockConfirm(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleBlockUser(blockConfirm.uid, blockConfirm.name)} danger>{t('admin.block')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* View routing */}
        {view === "new" ? (
          <NewProjectForm currentUser={user} currentUserData={userData} onSuccess={() => setView("feed")} />

        ) : view === "moderation" && isAdmin ? (
          <ModerationPanel
            projects={projects}
            onDelete={(id, name) => setConfirmDelete({ id, name })}
            onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
          />

        ) : (
          <>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.38em", color: C.blueGhost, textTransform: "uppercase", marginBottom: 6 }}>
                {feedMeta[view]?.pre}
              </p>
              <h1 style={{ fontFamily: "'Playfair Display',serif", color: C.blue, fontSize: 30, fontWeight: 700, margin: 0 }}>
                {feedMeta[view]?.title}
              </h1>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <div style={{ height: 1, flex: 1, background: C.border }} />
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.blueGhost, letterSpacing: "0.2em" }}>
                  {isPageLoading ? "…" : `${visibleProjects.length} ${t('boneforge.projects')}`}
                </span>
              </div>
            </div>

            {/* Yükleniyor */}
            {isPageLoading ? (
              <div style={{ textAlign: "center", padding: "80px 0" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", margin: "0 auto 20px", border: "2px solid rgba(82,162,255,0.15)", borderTopColor: "rgba(82,162,255,0.6)", animation: "bfg-spin 0.9s linear infinite" }} />
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueGhost, letterSpacing: "0.2em", marginBottom: 6 }}>
                  {t('boneforge.loadingArchive')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(82,162,255,0.18)", letterSpacing: "0.14em" }}>
                  {t('boneforge.loadingHint')}
                </p>
                <style>{`@keyframes bfg-spin { to { transform: rotate(360deg); } }`}</style>
              </div>

            ) : dbError ? (
              <div style={{ maxWidth: 480, margin: "60px auto", background: C.bgCard, border: `1px solid rgba(248,113,113,0.2)`, padding: "40px 36px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.5 }}>⚠</div>
                <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 10, fontWeight: 700 }}>{t('bonebeat.errorTitle')}</p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "rgba(248,113,113,0.5)", marginBottom: 24, lineHeight: 1.7, letterSpacing: "0.04em", wordBreak: "break-word" }}>{dbError}</p>
                <GhostBtn onClick={() => window.location.reload()}>{t('bonebeat.reload')}</GhostBtn>
              </div>

            ) : visibleProjects.length === 0 ? (
              view === "mine" ? (
                <div style={{ maxWidth: 480, margin: "60px auto", background: C.bgCard, border: `1px solid ${C.border}`, padding: "48px 40px", textAlign: "center" }}>
                  <div style={{ marginBottom: 24 }}>
                    <svg viewBox="0 0 24 24" fill="none" width={40} height={40} style={{ margin: "0 auto", display: "block", opacity: 0.3 }}>
                      <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.blueDim, marginBottom: 10, fontWeight: 700 }}>{t('boneforge.noProjects')}</p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueGhost, marginBottom: 28, lineHeight: 1.8, letterSpacing: "0.04em" }}>{t('boneforge.noProjectsHint')}</p>
                  <PrimaryBtn onClick={() => setView("new")}>⚒ {t('boneforge.addProject')}</PrimaryBtn>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.blueDim, marginBottom: 8 }}>{t('boneforge.archiveEmpty')}</p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.blueGhost, marginBottom: 20 }}>{t('boneforge.archiveEmptyHint')}</p>
                  <PrimaryBtn onClick={() => setView("new")}>⚒ {t('boneforge.addProject')}</PrimaryBtn>
                </div>
              )

            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
                {visibleProjects.map(p => (
                  <ForgeCard
                    key={p.id} project={p}
                    onDelete={(id, name) => setConfirmDelete({ id, name })}
                    onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
                    isAdmin={isAdmin} currentUid={user?.uid}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <style>{`
        .bf-mod-row {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) 130px;
          gap: 8px 12px;
        }
        @media (max-width: 640px) {
          .bf-mod-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
          .bf-mod-row > :first-child,
          .bf-mod-row > :nth-child(4) { grid-column: 1 / -1; }
        }
        ::-webkit-scrollbar { width: 4px; background: ${C.bgDeep}; }
        ::-webkit-scrollbar-thumb { background: rgba(82,162,255,0.14); }
        ::selection { background: rgba(82,162,255,0.2); color: ${C.white}; }
      `}</style>
    </div>
  );
}

// ── Dışa aktarım ──────────────────────────────────────────────────────────────
export default function BoneForge() {
  return (
    <SidebarForgeProvider>
      <BoneForgeInner />
    </SidebarForgeProvider>
  );
}
